// v2 smelter — a blast bloomery: a tall tapered stone chimney stack ringed with
// iron bands rising off a square hearth, a glowing throat at the top under a
// smoke puff, a tapping arch on the front with an inclined tap channel running
// glowing metal down to an ingot mould, and a big hand bellows on the east side
// blowing into the stack's foot. Ore and slag heaps dress the yard. Ported 1:1
// from age-of-mcp's v2/smelter.ts, using the library's shared `foundationSlab`,
// `scaffold` and `industry` (emberMouth) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, industry, benchWork, smokePlume, firePulse, EFFECT_LOOP, type Part } from "../../lib";

const STONE  = "#6f6f74";   // hearth + stack stone
const DARK   = "#4a4a4e";   // iron bands / mould
const FIRE   = "#f0842c";   // glowing metal
const EMBER  = "#ffd24a";   // bright ember core
const SMOKE  = "#b8b8b8";
const WOOD   = "#7a5a30";   // bellows boards
const LEATHER = "#8a4a2e";  // bellows hide
const ORE    = "#9a8a6a";   // ore chunks
const SLAG   = "#3a3a3e";   // slag heap

const BASE = 0.14;          // foundation slab top
const HEARTH_TOP = BASE + 0.20;   // 0.34

// The square stone hearth the stack stands on.
function hearth(): Part {
  return { geometry: new THREE.BoxGeometry(0.60, 0.20, 0.60), color: STONE,
    position: [-0.05, BASE + 0.10, -0.05] };
}

// The tapered chimney stack, full height or a partial build of `h`.
function stack(h: number): Part {
  const t = h / 0.95;
  return {
    geometry: new THREE.CylinderGeometry(0.24 - 0.10 * t, 0.24, h, 8),
    color: STONE, position: [-0.05, HEARTH_TOP + h / 2, -0.05],
  };
}

// Iron reinforcing bands ringing the finished stack.
function bands(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.215, 0.215, 0.035, 8), color: DARK,
      position: [-0.05, 0.55, -0.05] },
    { geometry: new THREE.CylinderGeometry(0.185, 0.185, 0.035, 8), color: DARK,
      position: [-0.05, 0.85, -0.05] },
  ];
}

// The glowing throat at the stack top; the rising smoke is the animated plume.
function throat(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.17, 0.16, 0.07, 8), color: DARK,
      position: [-0.05, 1.325, -0.05] },
    { geometry: new THREE.CylinderGeometry(0.11, 0.11, 0.025, 8), color: FIRE,
      position: [-0.05, 1.355, -0.05] },
    ...plume.parts,
  ];
}

// The "bloomery is running" cue: smoke off the throat, the ember mouth pulsing
// and the bellows' top board pumping about its nozzle-end hinge.
const plume = smokePlume([-0.05, 1.40, -0.05], { rise: 0.4, radius: 0.065, color: SMOKE });

// The tap channel running from the arch down to the ingot mould out front,
// with a streak of glowing metal in it.
function tapChannel(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.14, 0.05, 0.26), color: "#5a5248",
      position: [-0.05, 0.17, 0.40], rotation: [0.14, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.07, 0.02, 0.24), color: EMBER,
      position: [-0.05, 0.20, 0.40], rotation: [0.14, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.18, 0.05, 0.12), color: DARK,
      position: [-0.05, BASE + 0.03, 0.50] },
    { geometry: new THREE.BoxGeometry(0.12, 0.025, 0.07), color: FIRE,
      position: [-0.05, BASE + 0.065, 0.50] },
  ];
}

// The big hand bellows on the east side, nozzle into the stack's foot.
function bellows(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.05, 0.16, 0.05), color: WOOD,
      position: [0.38, BASE + 0.08, -0.05] },
    { geometry: new THREE.BoxGeometry(0.24, 0.04, 0.15), color: WOOD,
      position: [0.33, 0.30, -0.05] },
    { geometry: new THREE.BoxGeometry(0.20, 0.10, 0.13), color: LEATHER,
      position: [0.34, 0.36, -0.05], rotation: [0, 0, 0.10] },
    { geometry: new THREE.BoxGeometry(0.24, 0.04, 0.15), color: WOOD,
      position: [0.33, 0.43, -0.05], rotation: [0, 0, 0.22], name: "bellows-top" },
    { geometry: new THREE.CylinderGeometry(0.018, 0.03, 0.10, 5), color: DARK,
      position: [0.18, 0.29, -0.05], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.BoxGeometry(0.20, 0.025, 0.025), color: WOOD,
      position: [0.42, 0.50, -0.05], rotation: [0, 0, 0.5], name: "bellows-top" },
  ];
}

// Ore waiting on the west, slag heaped on the east.
function heaps(): Part[] {
  return [
    { geometry: new THREE.IcosahedronGeometry(0.06, 0), color: ORE,
      position: [-0.33, BASE + 0.05, 0.28], rotation: [0.3, 0.8, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.045, 0), color: ORE,
      position: [-0.26, BASE + 0.04, 0.35], rotation: [0.6, 1.7, 0.2] },
    { geometry: new THREE.ConeGeometry(0.10, 0.12, 6), color: SLAG,
      position: [0.32, BASE + 0.06, 0.28] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) return [foundationSlab(0.84), hearth(), ...scaffold(0.82, 0.6)];
  if (s === 2) {
    return [
      foundationSlab(0.84), hearth(), stack(0.5),
      ...industry.emberMouth(-0.05, 0.24, 0.26, 0.26, 0.18),
      ...scaffold(0.82, 1.1),
    ];
  }
  return [
    foundationSlab(0.84),
    hearth(),
    stack(0.95),
    ...bands(),
    ...throat(),
    ...industry.emberMouth(-0.05, 0.24, 0.26, 0.26, 0.18, "embers"),
    ...tapChannel(),
    ...bellows(),
    ...heaps(),
  ];
}

export const smelterV2 = defineModel({
  id: "smelter-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Smelter (v2)",
  tags: ["building","smelter","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly smelter.",
  description: "A stone furnace that smelts ore into iron ingots.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Bloomery running",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [
        ...plume.tracks,
        firePulse("embers", [-0.05, 0.24, 0.27]),
        // The bellows pump twice per loop, hinging at their nozzle end.
        {
          target: "bellows-top",
          pivot: [0.21, 0.43, -0.05],
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: EFFECT_LOOP * 0.25, value: [0, 0, -0.14], ease: "sine" },
            { t: EFFECT_LOOP * 0.5, value: [0, 0, 0], ease: "sine" },
            { t: EFFECT_LOOP * 0.75, value: [0, 0, -0.14], ease: "sine" },
            { t: EFFECT_LOOP, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
  stations: [
    { id: "bench", position: [-0.05, 0.14, 0.48], facing: Math.PI },
  ],
  interactions: [
    { id: "smelt", label: "Tending the furnace", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
